import React, { useEffect, useState } from "react";
import { auth, database } from "../firebase/firebase";
import { ref, onValue, update } from "firebase/database";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { AiOutlineCheckCircle, AiOutlineClockCircle } from "react-icons/ai";
import TradingViewWidget from "../components/TradingViewWidget";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }
      fetchUserData(currentUser.uid);
    });

    return () => unsubscribeAuth();
  }, [navigate]);

  const fetchUserData = (userId) => {
    const userRef = ref(database, `users/${userId}`);

    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val() || {};
      const deposits = data.deposits ? { ...data.deposits } : {};

      // Check and update expired deposits individually
      const currentDate = new Date();

      Object.entries(deposits).forEach(([key, deposit]) => {
        if (!deposit.date) return;

        const depositDate = new Date(deposit.date);
        const timeDiff = currentDate - depositDate;
        const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
        let isExpired = false;

        if ([2400, 2900, 3400].includes(deposit.usdAmount) && daysDiff >= 30) {
          isExpired = true;
        } else if (
          [900, 1400, 1900].includes(deposit.usdAmount) &&
          daysDiff >= 90
        ) {
          isExpired = true;
        } else if (deposit.usdAmount === 50000 && daysDiff >= 365) {
          isExpired = true;
        }

        if (isExpired && deposit.status !== "Expired") {
          update(ref(database, `users/${userId}/deposits/${key}`), {
            status: "Expired",
          });
        }
      });

      setUserData({
        username: data.username || "User",
        balance: data.balance || 0,
        deposits: Object.values(deposits),
        profits: data.profits ? Object.values(data.profits) : [],
        withdrawals: data.withdrawals ? Object.values(data.withdrawals) : [],
      });

      setLoading(false);
    });

    return () => unsubscribe();
  };

  const handleTabChange = (index) => {
    console.log(userData.profits);
    setActiveTab(index);
  };
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading || !userData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="bg-white shadow-md rounded-lg p-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">Hi, {userData.username}</h2>
        <button
          onClick={handleLogout}
          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition border-none"
        >
          Logout
        </button>
      </div>

      <div className="bg-white shadow-lg rounded-2xl p-6 mt-6 text-center">
        <h1 className="text-3xl font-bold text-gray-800">
          ${userData.balance.toFixed(2)}
        </h1>
        <div className="flex justify-center gap-3 mt-4">
          <button
            onClick={() => navigate("/subscribe")}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg shadow-md hover:bg-blue-700 transition"
          >
            Subscribe
          </button>
          <button
            onClick={() => navigate("/withdraw")}
            className="bg-green-500 text-white px-6 py-2 rounded-lg shadow-md hover:bg-green-600 transition"
          >
            Withdraw
          </button>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg p-6 mt-6">
        <h3 className="text-xl font-semibold">Active Plans:</h3>
        <div className="flex flex-wrap gap-2 mt-2">
          {userData.deposits
            .filter((deposit) => deposit.status === "Confirmed")
            .map((deposit, index) => (
              <span
                key={index}
                className="bg-blue-500 text-white px-4 py-1 rounded-full"
              >
                {deposit.plan} - ${deposit.usdAmount?.toFixed(2)}
              </span>
            ))}
        </div>
      </div>
      <div className="h-[600px] m-3">
        <TradingViewWidget />
      </div>
      <div className="bg-white shadow-lg rounded-lg p-6 mt-6">
        <div className="flex justify-around bg-gray-200 rounded-lg p-2 mb-4">
          {["Deposits", "Profits", "Withdrawals"].map((tab, index) => (
            <button
              key={index}
              onClick={() => handleTabChange(index)}
              className={`flex-1 text-center py-3 font-semibold rounded-lg transition ${
                activeTab === index
                  ? "bg-white shadow-md text-blue-600"
                  : "text-gray-600 hover:bg-gray-300"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="h-60 overflow-auto">
          {activeTab === 0 && (
            <TransactionList items={userData.deposits} type="deposit" />
          )}
          {activeTab === 1 && (
            <TransactionList items={userData.profits} type="profit" />
          )}
          {activeTab === 2 && <TransactionList items={userData.withdrawals} />}
        </div>
      </div>
    </div>
  );
};

const TransactionList = ({ items, type }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        No transactions found
      </div>
    );
  }

  return (
    <ul className="w-full">
      {items
        .slice()
        .reverse()
        .map((item, index) => (
          <li
            key={index}
            className="grid grid-cols-5 gap-4 p-3 border-b border-gray-300 text-sm sm:text-base items-center"
          >
            <span className="text-left truncate">
              ${item.usdAmount?.toFixed(2) || "0.00"}
            </span>
            <span className="text-center truncate">
              {item.date ? new Date(item.date).toLocaleDateString() : "N/A"}
            </span>
            {(type === "deposit" || type === "profit") && (
              <span className="text-center truncate">
                {item.plan || "No plan"}
              </span>
            )}
            {type === "deposit" && (
              <span className="text-center truncate">
                {item.btcAmount || "N/A"}
              </span>
            )}
            <span
              className={`text-center px-3 py-1 rounded-full flex items-center justify-center gap-1 ${
                item.status === "Confirmed"
                  ? "bg-green-500 text-white"
                  : item.status === "Expired"
                  ? "bg-red-500 text-white"
                  : "bg-gray-300 text-gray-700"
              }`}
            >
              {item.status === "Confirmed" ? (
                <AiOutlineCheckCircle className="text-lg sm:text-base" />
              ) : item.status === "Pending" ? (
                <AiOutlineClockCircle className="text-lg sm:text-base" />
              ) : null}
              <span className="hidden sm:inline">
                {item.status || "Pending..."}
              </span>
            </span>
          </li>
        ))}
    </ul>
  );
};

export default Dashboard;
